import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import FooterCTA from "../components/FooterCTA";
import useDocumentMeta from "../lib/useDocumentMeta";

const FAQS = [
    {
        id: "q1",
        q: "What services does Raj Graphics offer?",
        a: "We're a full‑service design and branding studio. Our work spans logo design, complete brand identity systems, packaging, website design, social media creatives, and on‑location signage — including glow sign boards, acrylic boards, vinyl printing and shop facades.",
    },
    {
        id: "q2",
        q: "How long does a logo design take?",
        a: "A focused logo project typically takes 5–7 working days from kickoff to delivery. Larger identity systems with multiple touchpoints usually take 2–3 weeks. We'll confirm an exact timeline after the brief.",
    },
    {
        id: "q3",
        q: "What is your pricing range?",
        a: "We offer transparent packages starting at ₹4,999 for a focused logo, ₹9,999 for the full Business Pack, ₹19,999 for our Premium Pack with packaging and signage, and custom enterprise pricing for end‑to‑end brand + website + ongoing support. See our Shop page for what's included.",
    },
    {
        id: "q4",
        q: "Do you offer revisions?",
        a: "Yes. Every package includes a defined number of revision rounds — Starter has 2, Business has 3, and Premium has 5. Beyond that, additional revisions can be added at a fair per‑round rate.",
    },
    {
        id: "q5",
        q: "Can you design signage and glow boards?",
        a: "Absolutely — signage is one of our specialities. We design and produce glow sign boards, acrylic 3D signs, vinyl flex, lobby signs and complete shop facades. We can either deliver print‑ready artwork or handle the full install on location.",
    },
    {
        id: "q6",
        q: "Do you work with clients outside India?",
        a: "Yes. For design‑only deliverables (logo, identity, packaging artwork, social creatives, websites) we work with clients globally over WhatsApp, email and video calls. Physical signage installation is currently India‑only.",
    },
    {
        id: "q7",
        q: "How do I place an order?",
        a: "The fastest way is to WhatsApp us with a quick brief, or fill out the strategy‑call form on the Contact page. We'll reply within a few business hours, share a tailored quote, and once you confirm and pay the booking fee, we kick off the project.",
    },
    {
        id: "q8",
        q: "What file formats will I receive?",
        a: "You'll receive print‑ready PDF, high‑resolution PNG and JPG, plus editable source files (AI/EPS for logos, PSD/Figma where relevant). Everything is delivered in a clean folder structure with naming you can hand off to any printer or developer.",
    },
];

const FAQPage = () => {
    useDocumentMeta({
        title: "FAQ — Design, Printing & Signage Questions | Raj Graphics Nagpur",
        description:
            "Frequently asked questions about Raj Graphics — services, timelines, pricing, revisions, signage and file formats.",
        canonical: "/faq",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        },
    });

    return (
        <main data-testid="faq-page" className="bg-[#050505]">
            {/* Hero */}
            <section className="relative pt-36 pb-12 md:pt-44 md:pb-16 overflow-hidden">
                <div className="absolute inset-0 grid-pattern radial-fade opacity-60 pointer-events-none" />
                <div className="absolute -top-32 right-0 w-[500px] h-[500px] rounded-full bg-gold/[0.07] blur-[120px] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-5">
                        Frequently Asked
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="font-poppins font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] max-w-3xl"
                        data-testid="faq-headline"
                    >
                        Got questions? <span className="gold-gradient-text">We've got answers.</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mt-6 font-inter text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed"
                    >
                        Everything you might want to know before working with the studio. Don't
                        see your question? Just WhatsApp us — we'll reply quickly.
                    </motion.p>
                </div>
            </section>

            {/* Accordion */}
            <section className="relative py-12 md:py-16">
                <div className="max-w-4xl mx-auto px-6 md:px-12">
                    <Accordion
                        type="single"
                        collapsible
                        className="w-full space-y-3"
                        data-testid="faq-accordion"
                    >
                        {FAQS.map((item, i) => (
                            <AccordionItem
                                key={item.id}
                                value={item.id}
                                data-testid={`faq-item-${item.id}`}
                                className="border border-white/5 rounded-2xl bg-white/[0.02] hover:border-gold/30 transition-colors px-5 md:px-6 [&[data-state=open]]:border-gold/40 [&[data-state=open]]:bg-white/[0.04]"
                            >
                                <AccordionTrigger
                                    data-testid={`faq-trigger-${item.id}`}
                                    className="font-poppins font-medium text-white text-left text-base md:text-lg py-5 hover:no-underline [&>svg]:text-gold [&>svg]:h-5 [&>svg]:w-5"
                                >
                                    <span className="flex items-start gap-4">
                                        <span className="font-inter text-xs text-gold pt-1.5 tabular-nums">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span>{item.q}</span>
                                    </span>
                                </AccordionTrigger>
                                <AccordionContent className="font-inter text-sm md:text-base text-zinc-400 leading-relaxed pb-5 pl-12">
                                    {item.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>

                    <div className="mt-12 text-center">
                        <p className="font-inter text-zinc-400 mb-5">
                            Still have a question?
                        </p>
                        <Link
                            to="/contact"
                            data-testid="faq-contact-cta"
                            className="inline-flex items-center gap-2 bg-gold hover:bg-[#C5A030] text-black font-medium font-inter px-7 py-3.5 rounded-full transition-colors"
                        >
                            Talk to the studio <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>

            <FooterCTA />
        </main>
    );
};

export default FAQPage;
